import React, { useState, useEffect } from 'react';
import GraphDisplay from "./GraphDisplay";
import CodeDisplay from './CodeDisplay';
import { getDatabase, ref, onValue } from "firebase/database";
import { normalizeInstructionText } from "../../helpers/generalHelpers";

function AnalysisBlock({ block, allBlocks, onBlockClick, onToggleCollapse, onRefresh, onDelete, onAddGraph, onRefreshMap, onScaleOut, onDeleteGraph, onRefreshCode, onFixCode, onMoveToPosition, positionDisabled = false }) {
  // Default active toggle to "codeOutput" if block.codeOutput exists, otherwise "instructions" if available.
  const [activeToggle, setActiveToggle] = useState(
    block.codeOutput ? 'codeOutput' : (block.instructions ? 'instructions' : '')
  );
  const [codeStream, setCodeStream] = useState("");
  
  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "refresh" or "delete"

  /* Dialog for extra graph instructions */
  const [showGraphDialog, setShowGraphDialog] = useState(false);
  const [graphNote, setGraphNote] = useState('');

  /* ── map state just below graph dialog state ── */
const [showMapDialog, setShowMapDialog] = useState(false);  
const [mapNote,       setMapNote]       = useState('');   

const [showCodeDialog, setShowCodeDialog] = useState(false);
const [codeNote,       setCodeNote]       = useState('');

const handleGraphDelete = (idx) => onDeleteGraph?.(block, idx);   

  useEffect(() => {
    if (block.id) {
      const db = getDatabase();
      const streamRef = ref(db, `/generated_code/${block.id}`);
      const unsubscribe = onValue(streamRef, (snapshot) => {
        const val = snapshot.val();
        if (val && typeof val === 'string' && val.length > 0) {
          setCodeStream(val);
        }
      });
      return () => unsubscribe();
    }
  }, [block.id]);

  // Toggle handler that ensures only one button is active at a time.
  const toggleActive = (toggleType, e) => {
    e.stopPropagation();
    if (activeToggle === toggleType) {
      setActiveToggle('');
    } else {
      setActiveToggle(toggleType);
    }
  };

  const toggleCollapse = (e) => {
    e.stopPropagation();
    if (onToggleCollapse) {
      onToggleCollapse(block, !block.collapsed);
    }
  };

  // Instead of immediate refresh or delete, open the confirm dialog.
  const requestRefresh = (e) => {
    e.stopPropagation();
    setConfirmAction("refresh");
    setShowConfirmDialog(true);
  };

  const requestDelete = (e) => {
    e.stopPropagation();
    setConfirmAction("delete");
    setShowConfirmDialog(true);
  };

  // When user cancels the confirmation dialog.
  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  // On confirmation, call the appropriate parent's callback.
  const handleConfirm = () => {
    if (confirmAction === "refresh" && onRefresh) {
      // Clear any live code stream before refresh.
      setCodeStream("");
      onRefresh(block);
    } else if (confirmAction === "delete" && onDelete) {
      onDelete(block);
    }
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  const handleBlockClick = () => {
    if (block.collapsed && onToggleCollapse) {
      onToggleCollapse(block, false);
    }
    if (onBlockClick) {
      onBlockClick(block);
    }
  };

  // Style for scenario tags.
  const tagStyle = {
    backgroundColor: '#007BFF',
    color: '#fff',
    padding: '2px 4px',
    marginRight: '4px',
    borderRadius: '4px',
    fontSize: '12px',
  };

  // Dark-themed style for the instructions block.
  const defaultInstructionsStyle = {
    marginBottom: '10px',
    whiteSpace: 'pre-wrap',
    padding: '10px',
    borderRadius: '4px',
    backgroundColor: '#2C2C2C',
    color: '#FFFFFF'
  };

  // Button style helper: active buttons are rendered in blue.
  const getToggleButtonStyle = (toggleType) => ({
    backgroundColor: activeToggle === toggleType ? '#007BFF' : '#3A3A3A',
    color: '#FFF',
    border: 'none',
    borderRadius: '4px',
    padding: '5px 10px',
    cursor: 'pointer'
  });

  const handleAddGraphClick = (e) => {
    e.stopPropagation();
    setGraphNote('');
    setShowGraphDialog(true);
  };
  const confirmAddGraph = () => {
    setShowGraphDialog(false);
    onAddGraph?.(block, graphNote);
  };

  const handleRefreshMapClick = (e) => { 
    e.stopPropagation();
    setMapNote('');
    setShowMapDialog(true);            // open dialog
  };
  
  const confirmMapRefresh = () => {     
    setShowMapDialog(false);
    onRefreshMap?.(block, mapNote);    // pass extra note
  };

  const handleRefreshCodeClick = (e) => {
    e.stopPropagation();
    setCodeNote('');
    setShowCodeDialog(true);
  };
  
  const confirmCodeRefresh = () => {
    setShowCodeDialog(false);
    if (hasError) {
      if (isV2CodeAgent) return;
      onFixCode?.(block, codeNote);      // parent decides what to do
    } else {
      onRefreshCode?.(block, codeNote);
    }
  };

  const hasError = block.codeOutput?.includes("Error");
  const isV2CodeAgent = block.codeAgentVersion === 'v2';
  const statusVisible = !!block.runStatus && !!block.runStatusText;
  const statusColor = block.runStatus === 'error' ? '#ff9d9d' : '#9cb0d0';
  const instructionsText = normalizeInstructionText(block.instructions);

  return (
    <div
      onClick={handleBlockClick}
      style={{
        border: '1px solid #444',
        borderRadius: '8px',
        padding: '10px',
        marginBottom: '10px',
        cursor: 'pointer',
        backgroundColor: '#2C2C2C',
        color: '#FFFFFF',
      }}
    >
      {/* Header Section: Query and scenario details */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ flex: 1 }}>
          {block.userQuery && (
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
              {block.userQuery}
            </div>
          )}
          {statusVisible && (
            <div style={{ marginTop: '6px', fontSize: '13px', color: statusColor }}>
              {block.runStatusText}
            </div>
          )}
          <div style={{ marginTop: '10px', fontSize: '14px', color: '#ccc' }}>
            {block.simulationId && <div>{block.simulationId}</div>}
            {block.selectedLinkIds && block.selectedLinkIds.length > 0 && (
              <div style={{ 
                marginTop: '10px',
                display: 'flex',
                flexWrap: 'wrap',  
                alignItems: 'center',
                gap: '6px' 
               }}>
                Selected roads: {block.selectedLinkIds.map((id) => (
                  <span key={id} style={tagStyle}>{id}</span>
                ))}
              </div>
            )}
            {block.selectedNodeIds && block.selectedNodeIds.length > 0 && (
              <div style={{ 
                marginTop: '10px',
                display: 'flex',
                flexWrap: 'wrap',  
                alignItems: 'center',
                gap: '6px' 
               }}>
                Selected nodes: {block.selectedNodeIds.map((id) => (
                  <span key={id} style={{ ...tagStyle, backgroundColor: '#28a745' }}>{id}</span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Refresh button */}
          <button
            onClick={requestRefresh}
            style={{
              backgroundColor: '#2C2C2C',
              color: '#FFF',
              border: 'none',
              borderRadius: '4px',
              padding: '5px 5px',
              cursor: 'pointer',
              fontSize: '24px',
              lineHeight: '1'
            }}
            title="Refresh Analysis"
          >
            ⟳
          </button>
          {/* Delete button */}
          <button
            onClick={requestDelete}
            style={{
              backgroundColor: '#2C2C2C',
              color: '#FFF',
              border: 'none',
              borderRadius: '4px',
              padding: '5px 5px',
              cursor: 'pointer',
              fontSize: '16px',
              lineHeight: '1'
            }}
            title="Delete Analysis"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="#FFFFFF" height="20" width="20" viewBox="0 0 20 20">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-4.5l-1-1z"/>
            </svg>

          </button>
          {/* Collapse button */}
          <button
            onClick={toggleCollapse}
            style={{
              backgroundColor: '#2C2C2C',
              color: '#FFF',
              border: 'none',
              borderRadius: '4px',
              padding: '5px 5px',
              cursor: 'pointer',
              fontSize: '16px',
              lineHeight: '1'
            }}
            title="Collapse/Expand"
          >
            {block.collapsed ? '▼' : '▲'}
          </button>
        </div>
      </div>

      {/* Content Section (when not collapsed) */}
      {!block.collapsed && (
        <>
          {/* Toggle Button Group */}
          {(block.codeOutput || codeStream || block.code || block.instructions) && (
            <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
              {(block.codeOutput || codeStream) && (
                <button
                  onClick={(e) => toggleActive('codeOutput', e)}
                  style={getToggleButtonStyle('codeOutput')}
                >
                  Show Code Output
                </button>
              )}
              {block.code && (
                <button
                  onClick={(e) => toggleActive('generatedCode', e)}
                  style={getToggleButtonStyle('generatedCode')}
                >
                  Show Generated Code
                </button>
              )}
              {block.instructions && (
                <button
                  onClick={(e) => toggleActive('instructions', e)}
                  style={getToggleButtonStyle('instructions')}
                >
                  Show Instructions
                </button>
              )}
              <select
                value={Math.max(1, allBlocks.findIndex((item) => String(item.id) === String(block.id)) + 1)}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => {
                  event.stopPropagation();
                  onMoveToPosition?.(block, Number(event.target.value));
                }}
                disabled={positionDisabled}
                title={positionDisabled ? 'Clear the filter to change position' : 'Move block to position'}
                aria-label="Analysis block position"
                style={{
                  backgroundColor: '#2C2C2C',
                  color: '#FFF',
                  border: '1px solid #555',
                  borderRadius: '4px',
                  cursor: positionDisabled ? 'not-allowed' : 'pointer',
                  padding: '5px'
                }}
              >
                {allBlocks.map((_, index) => (
                  <option key={index + 1} value={index + 1}>
                    Position {index + 1}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Conditional Rendering Based on Active Toggle */}
          {activeToggle === 'codeOutput' && (
            <>
              {block.codeOutput ? (
                <div style={{ marginBottom: '10px' }}>
                  <CodeDisplay title="Code Output" code={block.codeOutput} />
                </div>
              ) : codeStream ? (
                <div style={{ marginBottom: '10px' }}>
                  <CodeDisplay title="Live Code Stream" code={codeStream} autoScroll={true} />
                </div>
              ) : null}
            </>
          )}
          {activeToggle === 'generatedCode' && block.code && (
            <div style={{ marginBottom: '10px' }}>
              <CodeDisplay title="Generated Code" code={block.code} />
            </div>
          )}
          {activeToggle === 'instructions' && block.instructions && (
            <div style={defaultInstructionsStyle}>
              <strong>Instructions:</strong>
              <div>{instructionsText}</div>
            </div>
          )}

          {/* Default fallback if no toggle is active */}
          {!activeToggle && (
            <>
              {block.codeOutput ? (
                <div style={{ marginBottom: '10px' }}>
                  <CodeDisplay title="Code Output" code={block.codeOutput} />
                </div>
              ) : codeStream ? (
                <div style={{ marginBottom: '10px' }}>
                  <CodeDisplay title="Live Code Stream" code={codeStream} autoScroll={true} />
                </div>
              ) : block.instructions ? (
                <div style={defaultInstructionsStyle}>
                  <strong>Instructions:</strong>
                  <div>{instructionsText}</div>
                </div>
              ) : null}
            </>
          )}

          {block.instructions ? (
              <div style={{ marginBottom:10, display:'flex', gap:10 }}>
                {block.codeOutput?
                <>
                <button style={{ ...getToggleButtonStyle('00'), background:'#007BFF' }} title="Refresh map" onClick={handleRefreshMapClick}>Refresh Map</button>
                <button style={{ ...getToggleButtonStyle('00'), background:'#007BFF' }} title="Refresh graph" onClick={handleAddGraphClick}>Add Graph</button>
                </>
                : null}
                {!(hasError && isV2CodeAgent) && (
                  <button
                    style={{ ...getToggleButtonStyle('00'), background: '#007BFF' }}
                    title={hasError ? 'Fix code' : 'Refresh code'}
                    onClick={handleRefreshCodeClick}
                  >
                    {hasError ? 'Fix Code' : 'Refresh Code'}
                  </button>
                )}
                <button style={{ ...getToggleButtonStyle('00'), background:'#007BFF' }} title="Scale" onClick={() => onScaleOut(block)}>Scale</button>
              </div>
            ): null
          }

          {block.graphs && (
            <div style={{ marginBottom: '10px' }}>
              <GraphDisplay graphs={block.graphs} allBlocks={allBlocks} onDeleteGraph={handleGraphDelete}/>
            </div>
          )}
        </>
      )}

      {/* Confirmation Modal Dialog */}
      {showConfirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#1E1E1E',
            padding: '20px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
            color: '#fff',
          }}>
            <h3>{confirmAction === "refresh" ? "Confirm Refresh" : "Confirm Delete"}</h3>
            <p>
              {confirmAction === "refresh"
                ? "Do you want to refresh this analysis block?"
                : "Do you want to delete this analysis block?"}
            </p>
            <div style={{ marginTop: '10px', textAlign: 'right' }}>
              <button
                onClick={handleCancelConfirm}
                style={{
                  marginRight: '10px',
                  padding: '6px 12px',
                  backgroundColor: '#777',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#007BFF',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}


      {/* 🔸 NEW Graph modal for “additional instructions” */}
      {showGraphDialog && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.8)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000
        }}>
          <div style={{
            background:'#1E1E1E', padding:20, borderRadius:8,
            width:'90%', maxWidth:400, color:'#fff'
          }}>
            <h3>Any additional instructions for graph?</h3>

            <textarea
              value={graphNote}
              onChange={e => setGraphNote(e.target.value)}
              rows={4}
              style={{
                width:'100%', background:'#2C2C2C', color:'#fff',
                border:'1px solid #555', borderRadius:4, padding:6, resize:'vertical'
              }}
              placeholder="(optional) e.g. show only top 5 values…"
            />

            <div style={{ marginTop:10, textAlign:'right' }}>
              <button
                onClick={()=>setShowGraphDialog(false)}
                style={{
                  marginRight:10, padding:'6px 12px', background:'#777',
                  color:'#fff', border:'none', borderRadius:4, cursor:'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmAddGraph}
                style={{
                  padding:'6px 12px', background:'#007BFF', color:'#fff',
                  border:'none', borderRadius:4, cursor:'pointer'
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {showMapDialog && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.8)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000
        }}>
          <div style={{
            background:'#1E1E1E', padding:20, borderRadius:8,
            width:'90%', maxWidth:400, color:'#fff'
          }}>
            <h3>Any additional instructions for map?</h3>

            <textarea
              value={mapNote}
              onChange={e => setMapNote(e.target.value)}
              rows={4}
              style={{
                width:'100%', background:'#2C2C2C', color:'#fff',
                border:'1px solid #555', borderRadius:4, padding:6, resize:'vertical'
              }}
              placeholder="(optional) e.g. highlight only congested links…"
            />

            <div style={{ marginTop:10, textAlign:'right' }}>
              <button
                onClick={()=>setShowMapDialog(false)}
                style={{
                  marginRight:10, padding:'6px 12px', background:'#777',
                  color:'#fff', border:'none', borderRadius:4, cursor:'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmMapRefresh}          /* ⬅ confirm */
                style={{
                  padding:'6px 12px', background:'#007BFF', color:'#fff',
                  border:'none', borderRadius:4, cursor:'pointer'
                }}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {showCodeDialog && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.8)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000
        }}>
          <div style={{
            background:'#1E1E1E', padding:20, borderRadius:8,
            width:'90%', maxWidth:400, color:'#fff'
          }}>
            <h3>Any additional instructions for code?</h3>

            <textarea
              value={codeNote}
              onChange={e => setCodeNote(e.target.value)}
              rows={4}
              style={{
                width:'100%', background:'#2C2C2C', color:'#fff',
                border:'1px solid #555', borderRadius:4, padding:6, resize:'vertical'
              }}
              placeholder="(optional) e.g. show only top 5 values…"
            />

            <div style={{ marginTop:10, textAlign:'right' }}>
              <button
                onClick={()=>setShowCodeDialog(false)}
                style={{
                  marginRight:10, padding:'6px 12px', background:'#777',
                  color:'#fff', border:'none', borderRadius:4, cursor:'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmCodeRefresh}
                style={{
                  padding:'6px 12px', background:'#007BFF', color:'#fff',
                  border:'none', borderRadius:4, cursor:'pointer'
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalysisBlock;
